import React, { useState, useEffect } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Label, UncontrolledDropdown, 
    DropdownToggle, DropdownMenu, DropdownItem, Spinner, Modal, ModalHeader, ModalBody, UncontrolledCarousel 
} from 'reactstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { APIClient } from '../../helpers/api_helper';
import { toast } from 'react-toastify';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import UserDetailsModal from '../../Components/Common/UserDetailsModal';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '');

const EmailLogs = () => {
    document.title = "Email Logs";
    
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 10,
        totalCount: 0
    });
    const [dateLogsModal, setDateLogsModal] = useState(false);
    const [selectedDateLogs, setSelectedDateLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [userDetailsModal, setUserDetailsModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(false);

    const fetchLogs = async (page = 1) => {
        try {
            setLoading(true);
            const skip = (page - 1) * pagination.limit;
            let url = `${API_BASE_URL}/emailLogs?skip=${skip}&limit=${pagination.limit}`;
            
            if (searchText) {
                url += `&searchText=${encodeURIComponent(searchText)}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                }
            });

            const data = await response.json();
            
            if (data.data) {
                setLogs(data.data);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: data.recordsTotal
                }));
            }
        } catch (error) {
            console.error('Error fetching email logs:', error);
            toast.error("Failed to fetch email logs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchLogs(1);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchText]);

    // Fetch user details when a username is clicked
    const fetchUserDetails = async (userId) => {
        if (!userId) {
            toast.error("User ID not available");
            return;
        }
        
        try {
            setLoadingUser(true);
            const authUser = JSON.parse(sessionStorage.getItem("authUser"));
            const contactMasked = authUser?.userContactMasked ? 1 : 0;
            
            const response = await APIClient.get(`/python/users/?userId=${userId}&contactMasked=${contactMasked}`);
            
            if (response.data && response.data.result && response.data.result.length > 0) {
                setSelectedUser(response.data.result[0]);
                setUserDetailsModal(true);
            } else {
                toast.error("User details not found");
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
            toast.error("Failed to fetch user details");
        } finally {
            setLoadingUser(false);
        }
    };

    // Helper function to get image URL
    const getImageUrl = (url) => {
        if (!url) return '/images/default-avatar.png';
        if (url.startsWith('http')) return url;
        
        const baseUrl = process.env.REACT_APP_MEDIA_URL.replace(/\/$/, '');
        const imagePath = url.replace(/^\//, '');
        return `${baseUrl}/${imagePath}`;
    };

    const renderPagination = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        
        // Function to get page numbers to show
        const getPageNumbers = () => {
            const delta = 2; // Number of pages to show before and after current page
            const range = [];
            const rangeWithDots = [];
            let l;

            for (let i = 1; i <= totalPages; i++) {
                if (i === 1 || i === totalPages || 
                    (i >= pagination.currentPage - delta && i <= pagination.currentPage + delta)) {
                    range.push(i);
                }
            }

            for (let i of range) {
                if (l) {
                    if (i - l === 2) {
                        rangeWithDots.push(l + 1);
                    } else if (i - l !== 1) {
                        rangeWithDots.push('...');
                    }
                }
                rangeWithDots.push(i);
                l = i;
            }

            return rangeWithDots;
        };

        return (
            <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <Col sm={4}>
                    <div className="text-muted">
                        Showing <span className="fw-semibold">{Math.min((pagination.currentPage - 1) * pagination.limit + 1, pagination.totalCount)}-{Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}</span> of <span className="fw-semibold">{pagination.totalCount}</span> Results
                    </div>
                </Col>
                <Col sm={8}>
                    <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                        <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => fetchLogs(pagination.currentPage - 1)}>
                                <i className="ri-arrow-left-s-line"></i>
                            </button>
                        </li>
                        
                        {getPageNumbers().map((pageNumber, index) => (
                            <li 
                                className={`page-item ${pageNumber === pagination.currentPage ? 'active' : ''} ${pageNumber === '...' ? 'disabled' : ''}`} 
                                key={index}
                            >
                                <button 
                                    className="page-link"
                                    onClick={() => pageNumber !== '...' && fetchLogs(pageNumber)}
                                >
                                    {pageNumber}
                                </button>
                            </li>
                        ))}

                        <li className={`page-item ${pagination.currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => fetchLogs(pagination.currentPage + 1)}>
                                <i className="ri-arrow-right-s-line"></i>
                            </button>
                        </li>
                    </ul>
                </Col>
            </Row>
        );
    };

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Email Logs" pageTitle="Logs" />
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="border-bottom-dashed">
                                <Row className="g-3">
                                    <Col xxl={3} lg={6}>
                                        <div className="search-box">
                                            <Input
                                                type="text"
                                                className="form-control search"
                                                placeholder="Search by username..."
                                                value={searchText}
                                                onChange={(e) => setSearchText(e.target.value)}
                                            />
                                            <i className="ri-search-line search-icon"></i>
                                        </div>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <div className="table-responsive">
                                    <Table className="table-striped table-nowrap align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '80px' }}>Sl. No</th>
                                                <th>Date & Time</th>
                                                <th>Username</th>
                                                <th>Email ID</th>
                                                <th>Trigger</th>
                                                <th>Email API</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.map((log, index) => (
                                                <tr key={log._id}>
                                                    <td>
                                                        {((pagination.currentPage - 1) * pagination.limit) + index + 1}
                                                    </td>
                                                    <td>{moment(log.sentOn).format('DD MMM YYYY, hh:mm A')}</td>
                                                    <td>
                                                        {log.userId ? (
                                                            <Button
                                                                color="link"
                                                                className="p-0 text-body fw-medium"
                                                                onClick={() => fetchUserDetails(log.userId)}
                                                            >
                                                                {log.userName || 'N/A'}
                                                            </Button>
                                                        ) : (
                                                            log.userName || 'N/A'
                                                        )}
                                                    </td>
                                                    <td>{log.emailId}</td>
                                                    <td>
                                                        <span className={`badge bg-${
                                                            log.trigger === 'LOGIN_WITH_EMAIL' ? 'info' :
                                                            log.trigger === 'INACTIVE_ACCOUNT' ? 'warning' :
                                                            'success'
                                                        }-subtle text-${
                                                            log.trigger === 'LOGIN_WITH_EMAIL' ? 'info' :
                                                            log.trigger === 'INACTIVE_ACCOUNT' ? 'warning' :
                                                            'success'
                                                        }`}>
                                                            {log.trigger}
                                                        </span>
                                                    </td>
                                                    <td>{log.emailAPI}</td>
                                                    <td>
                                                        <span className={`badge bg-${log.status === 'pending' ? 'warning' : 'success'}-subtle text-${log.status === 'pending' ? 'warning' : 'success'}`}>
                                                            {log.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {logs.length === 0 && (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4 text-muted">
                                                        <i className="ri-information-line fs-4 me-1 align-middle"></i>
                                                        No email logs found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                                {logs.length > 0 && renderPagination()}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            {loading && (
                <div className="overlay">
                    <div className="spinner-container">
                        <Spinner color="primary" />
                    </div>
                </div>
            )}
            <UserDetailsModal 
                isOpen={userDetailsModal}
                toggle={() => setUserDetailsModal(false)}
                user={selectedUser}
                loading={loadingUser}
                getImageUrl={getImageUrl}
            />
        </div>
    );
};

export default EmailLogs;